import { env } from './env';
import { resolveAppOrigin } from './runtime-url';

const PRIVATE_LAN_HOST =
  /^(192\.168\.\d{1,3}\.\d{1,3}|10\.\d{1,3}\.\d{1,3}\.\d{1,3}|172\.(1[6-9]|2\d|3[01])\.\d{1,3}\.\d{1,3})$/;

const BACKEND_PORT = 4000;
const FRONTEND_PORT = 5173;

function isLocalhostHost(host: string): boolean {
  return host === 'localhost' || host === '127.0.0.1';
}

/** Current page hostname (never hard-code localhost in client URLs). */
export function getPageHostname(): string {
  if (typeof window === 'undefined') return '';
  return window.location.hostname;
}

export function pageIsLanHost(): boolean {
  const host = getPageHostname();
  if (!host || isLocalhostHost(host)) return false;
  return PRIVATE_LAN_HOST.test(host);
}

function normalizeOrigin(url: string): string {
  return url.replace(/\/api\/v1\/?$/, '').replace(/\/$/, '');
}

function envPointsAtLocalhost(raw: string): boolean {
  try {
    return isLocalhostHost(new URL(raw).hostname);
  } catch {
    return false;
  }
}

/**
 * Dev: prefer same-origin Vite proxy (HTTPS frontend → HTTP backend on host).
 * Prod: explicit env URLs.
 */
export function shouldUseDevProxy(): boolean {
  if (!import.meta.env.DEV || typeof window === 'undefined') return false;

  const explicitApi = env.VITE_API_URL?.trim();
  const explicitSocket = env.VITE_SOCKET_URL?.trim();
  if (explicitApi || explicitSocket) {
    const raw = explicitApi || explicitSocket!;
    if (envPointsAtLocalhost(raw) && pageIsLanHost()) return true;
    return false;
  }

  return true;
}

/** Backend origin for direct connections (when proxy is off). */
export function getBackendOrigin(): string {
  const explicit = env.VITE_API_URL?.trim() || env.VITE_SOCKET_URL?.trim();
  if (explicit) return normalizeOrigin(explicit);

  if (typeof window === 'undefined') {
    return `http://127.0.0.1:${BACKEND_PORT}`;
  }

  const host = getPageHostname();
  const protocol = window.location.protocol === 'https:' ? 'https:' : 'http:';
  return `${protocol}//${host}:${BACKEND_PORT}`;
}

/** Socket.IO namespace origin (no path). */
export function getSocketOrigin(): string {
  if (!import.meta.env.DEV) {
    return resolveAppOrigin(env.VITE_SOCKET_URL);
  }

  const explicit = env.VITE_SOCKET_URL?.trim();
  if (explicit) return normalizeOrigin(explicit);

  if (shouldUseDevProxy()) {
    return window.location.origin;
  }

  return getBackendOrigin();
}

/** REST API base — empty string uses same-origin `/api` proxy in dev. */
export function getApiBaseUrl(): string {
  if (import.meta.env.DEV && shouldUseDevProxy()) return '';
  const explicit = env.VITE_API_URL?.trim();
  if (explicit) return normalizeOrigin(explicit);
  return getBackendOrigin();
}

export const DEV_PORTS = { frontend: FRONTEND_PORT, backend: BACKEND_PORT } as const;
